import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Shield, Mail, MapPin, Phone } from "lucide-react";

const EFFECTIVE_DATE = "February 1, 2026";
const COMPANY = "Kosansh Solutions Inc";
const DBA = "ActivitySports.com";
const EMAIL = "techieroshan@gmail.com";
const ADDRESS = "Fort Worth, TX, United States";

const sections = [
  { id: "introduction", title: "1. Introduction" },
  { id: "controller", title: "2. Data Controller" },
  { id: "data-collected", title: "3. Data We Collect" },
  { id: "how-we-use", title: "4. How We Use Your Data" },
  { id: "legal-basis", title: "5. Legal Basis for Processing" },
  { id: "data-sharing", title: "6. Data Sharing & Third Parties" },
  { id: "retention", title: "7. Data Retention" },
  { id: "your-rights", title: "8. Your Privacy Rights" },
  { id: "cookies", title: "9. Cookies & Tracking" },
  { id: "security", title: "10. Data Security" },
  { id: "children", title: "11. Children's Privacy" },
  { id: "international", title: "12. International Transfers" },
  { id: "changes", title: "13. Changes to This Policy" },
  { id: "contact", title: "14. Contact Us" },
];

export default function PrivacyPolicyPage() {
  const [active, setActive] = useState("introduction");
  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      {/* Hero */}
      <div className="bg-brand-navy text-white py-14 px-4 dark-section">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-2 text-brand-green text-sm font-bold uppercase tracking-widest mb-3">
            <Shield size={14} /> Legal
          </div>
          <h1 className="text-4xl md:text-5xl font-hero font-black mb-3">Privacy Policy</h1>
          <p className="text-white/70 max-w-2xl">
            {COMPANY} is committed to protecting your personal data. This policy explains what we collect,
            how we use it, and your rights under GDPR, CCPA, and applicable US privacy law.
          </p>
          <p className="text-white/50 text-sm mt-4">Effective: {EFFECTIVE_DATE} &nbsp;·&nbsp; Last Updated: {EFFECTIVE_DATE}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12 flex flex-col lg:flex-row gap-10">
        {/* Sidebar TOC */}
        <aside className="lg:w-64 flex-shrink-0">
          <div className="bg-white border border-gray-200 rounded-2xl p-5 sticky top-24 shadow-sm">
            <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4">Contents</p>
            <nav className="space-y-1">
              {sections.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  onClick={() => setActive(s.id)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                    active === s.id
                      ? "bg-brand-navy/5 text-brand-navy font-semibold"
                      : "text-gray-500 hover:text-brand-navy hover:bg-gray-50"
                  }`}
                >
                  <ChevronRight size={12} className="flex-shrink-0" />
                  {s.title}
                </a>
              ))}
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <article className="flex-1 max-w-3xl space-y-12">

          <Section id="introduction" title="1. Introduction">
            <p>
              Welcome to {DBA}, operated by <strong>{COMPANY}</strong> ("Company," "we," "us," or "our"),
              a Texas corporation headquartered in {ADDRESS}. We operate the ActivitySports.com platform
              (the "Service"), which connects sports players for partner matching, venue discovery, session
              booking, and community engagement.
            </p>
            <p className="mt-3">
              This Privacy Policy ("Policy") describes how we collect, use, disclose, and safeguard
              your personal information when you use our website, mobile web application, and related
              services (collectively, the "Platform"). Please read this Policy carefully. By accessing
              or using the Platform, you agree to the practices described herein. If you do not agree,
              please discontinue use of the Platform immediately.
            </p>
            <p className="mt-3">
              This Policy applies to all users globally. Users in the European Economic Area (EEA) and
              California have additional rights described in Sections 8 and 5 respectively.
            </p>
          </Section>

          <Section id="controller" title="2. Data Controller">
            <p>
              The data controller responsible for your personal data is:
            </p>
            <div className="mt-4 bg-gray-50 border border-gray-200 rounded-xl p-5 space-y-2">
              <p className="font-bold text-brand-navy">{COMPANY}</p>
              <p className="text-sm text-gray-600">Operating as: {DBA}</p>
              <div className="flex items-center gap-2 text-sm text-gray-600"><MapPin size={13} />{ADDRESS}</div>
              <div className="flex items-center gap-2 text-sm text-gray-600"><Mail size={13} />{EMAIL}</div>
            </div>
            <p className="mt-3">
              For EEA residents, {COMPANY} acts as a data controller under the General Data Protection
              Regulation (EU) 2016/679 ("GDPR"). We do not currently have a registered EU representative
              but will appoint one if required by applicable law.
            </p>
          </Section>

          <Section id="data-collected" title="3. Data We Collect">
            <SubHead>3.1 Data You Provide Directly</SubHead>
            <ul className="list-disc pl-5 space-y-1 text-gray-700 text-sm">
              <li><strong>Registration data:</strong> Name, email address, password (hashed), city of residence</li>
              <li><strong>Profile data:</strong> Bio, sports played, skill levels (NTRP, DUPR, handicap, etc.), profile photo, gender, spoken languages, play intent, equipment ownership</li>
              <li><strong>Photo Verification:</strong> Photo URL submitted for identity verification review</li>
              <li><strong>Communications:</strong> Messages sent between users via the in-app messaging system</li>
              <li><strong>Session data:</strong> Play session invitations, acceptances, outcomes (completed/cancelled)</li>
              <li><strong>League data:</strong> Corporate league participation, match results, standings</li>
              <li><strong>Payment data:</strong> If you subscribe to a paid tier, billing information is processed by third-party payment processors (we do not store raw card data)</li>
            </ul>

            <SubHead>3.2 Data Collected Automatically</SubHead>
            <ul className="list-disc pl-5 space-y-1 text-gray-700 text-sm">
              <li><strong>Usage data:</strong> Pages visited, features used, search queries, filter settings, time spent</li>
              <li><strong>Device data:</strong> IP address, browser type, operating system, device identifiers</li>
              <li><strong>Log data:</strong> HTTP requests, error logs, response times (stored for up to 90 days)</li>
              <li><strong>Cookies and similar technologies:</strong> Session cookies, analytics cookies (see Section 9)</li>
            </ul>

            <SubHead>3.3 Data from Third Parties</SubHead>
            <ul className="list-disc pl-5 space-y-1 text-gray-700 text-sm">
              <li><strong>Analytics providers:</strong> We use PostHog for product analytics (session recording, heatmaps)</li>
              <li><strong>AI providers:</strong> Match explanation features may send anonymized profile data to Google Gemini AI</li>
            </ul>
          </Section>

          <Section id="how-we-use" title="4. How We Use Your Data">
            {/* 1.4.10 Reflow: contained + keyboard-scrollable at 320px */}
            <div
              className="w-full overflow-x-auto focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-navy rounded-lg"
              tabIndex={0}
              role="region"
              aria-label="Data categories and purposes"
              data-testid="privacy-table-scroll"
            >
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-brand-navy/5">
                  <th className="text-left p-3 font-semibold text-brand-navy border border-gray-200">Purpose</th>
                  <th className="text-left p-3 font-semibold text-brand-navy border border-gray-200">Data Used</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[
                  ["Provide the core partner matching service", "Profile data, sport preferences, location, skill levels"],
                  ["Account creation and authentication", "Email, password hash, name"],
                  ["Enable in-app messaging", "Messages, user IDs, timestamps"],
                  ["Session booking and tracking", "Session invitations, venue, date/time, outcomes"],
                  ["AI-powered match explanations", "Anonymized sport/skill data sent to Gemini AI"],
                  ["Photo identity verification", "Photo URL, verification status"],
                  ["Send transactional emails (session invites, verification status)", "Email address via Emailit"],
                  ["Analytics and product improvement", "Usage data via PostHog"],
                  ["Enforce Community Guidelines and Terms of Service", "All profile and activity data"],
                  ["Respond to support requests", "Contact form submissions, email"],
                  ["Legal compliance and fraud prevention", "IP addresses, account activity"],
                  ["Corporate wellness leagues", "Work email (optional), league results, standings"],
                ].map(([purpose, data]) => (
                  <tr key={purpose} className="hover:bg-gray-50">
                    <td className="p-3 text-gray-700 border border-gray-200">{purpose}</td>
                    <td className="p-3 text-gray-500 border border-gray-200">{data}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </Section>

          <Section id="legal-basis" title="5. Legal Basis for Processing (GDPR & CCPA)">
            <SubHead>5.1 GDPR Legal Basis (EEA Users)</SubHead>
            <ul className="list-disc pl-5 space-y-1 text-gray-700 text-sm">
              <li><strong>Contract performance (Art. 6(1)(b)):</strong> Processing necessary to deliver the Service you signed up for</li>
              <li><strong>Legitimate interests (Art. 6(1)(f)):</strong> Analytics, fraud prevention, security, improving features</li>
              <li><strong>Consent (Art. 6(1)(a)):</strong> Marketing communications, optional analytics cookies</li>
              <li><strong>Legal obligation (Art. 6(1)(c)):</strong> Compliance with applicable law</li>
            </ul>
            <SubHead>5.2 California Rights (CCPA/CPRA)</SubHead>
            <p className="text-sm text-gray-700">
              California residents have the right to: (a) know what personal information is collected;
              (b) know whether personal information is sold or disclosed; (c) opt out of the sale of
              personal information (we do not sell personal information); (d) access their personal
              information; (e) request deletion; (f) not be discriminated against for exercising these rights.
            </p>
            <p className="mt-2 text-sm text-gray-700">
              To exercise CCPA rights, email us at <a href={`mailto:${EMAIL}`} className="text-brand-green underline hover:underline">{EMAIL}</a> with subject line "CCPA Request."
            </p>
          </Section>

          <Section id="data-sharing" title="6. Data Sharing & Third Parties">
            <p>We do <strong>not sell, rent, or broker</strong> your personal data to third parties for marketing purposes. We share data only in the following circumstances:</p>
            {/* 1.4.10 Reflow: contained + keyboard-scrollable at 320px */}
            <div
              className="w-full overflow-x-auto focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-navy rounded-lg"
              tabIndex={0}
              role="region"
              aria-label="Data sharing and safeguards"
              data-testid="privacy-table-scroll"
            >
            <table className="w-full text-sm border-collapse mt-4">
              <thead>
                <tr className="bg-brand-navy/5">
                  <th className="text-left p-3 font-semibold text-brand-navy border border-gray-200">Recipient</th>
                  <th className="text-left p-3 font-semibold text-brand-navy border border-gray-200">Purpose</th>
                  <th className="text-left p-3 font-semibold text-brand-navy border border-gray-200">Safeguards</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Other ActivitySports users", "Displaying your public profile for partner matching", "You control what's on your profile"],
                  ["Google Gemini AI", "Generating match explanations (anonymized)", "Data Processing Agreement"],
                  ["Emailit", "Transactional email delivery", "Data Processing Agreement"],
                  ["PostHog", "Product analytics and session recording", "Data Processing Agreement; EU data residency option"],
                  ["MongoDB Atlas (Cloud hosting)", "Database hosting", "SOC 2 Type II certified"],
                  ["Law enforcement / courts", "When required by law, subpoena, or court order", "Only to legal minimum"],
                  ["Acquirer in M&A transaction", "Business sale or merger", "Users notified in advance"],
                ].map(([r, p, s]) => (
                  <tr key={r} className="hover:bg-gray-50">
                    <td className="p-3 text-gray-700 border border-gray-200 font-medium">{r}</td>
                    <td className="p-3 text-gray-500 border border-gray-200">{p}</td>
                    <td className="p-3 text-gray-500 border border-gray-200">{s}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </Section>

          <Section id="retention" title="7. Data Retention">
            {/* 1.4.10 Reflow: contained + keyboard-scrollable at 320px */}
            <div
              className="w-full overflow-x-auto focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-navy rounded-lg"
              tabIndex={0}
              role="region"
              aria-label="Data retention periods"
              data-testid="privacy-table-scroll"
            >
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-brand-navy/5">
                  <th className="text-left p-3 font-semibold text-brand-navy border border-gray-200">Data Type</th>
                  <th className="text-left p-3 font-semibold text-brand-navy border border-gray-200">Retention Period</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Active account data", "For the lifetime of your account"],
                  ["Deleted account data", "30 days (grace period), then purged from primary databases"],
                  ["Messages", "2 years from message date, then anonymized"],
                  ["Server logs", "90 days"],
                  ["Payment records", "7 years (tax and legal compliance)"],
                  ["Photo verification submissions", "90 days after decision (approved or rejected)"],
                  ["Analytics data (PostHog)", "12 months rolling"],
                  ["Backup snapshots", "90 days"],
                ].map(([d, r]) => (
                  <tr key={d} className="hover:bg-gray-50">
                    <td className="p-3 text-gray-700 border border-gray-200 font-medium">{d}</td>
                    <td className="p-3 text-gray-500 border border-gray-200">{r}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </Section>

          <Section id="your-rights" title="8. Your Privacy Rights">
            <SubHead>8.1 Rights Available to All Users</SubHead>
            <ul className="list-disc pl-5 space-y-2 text-gray-700 text-sm">
              <li><strong>Access:</strong> Request a copy of the personal data we hold about you</li>
              <li><strong>Correction:</strong> Update inaccurate or incomplete data via your Account Settings page</li>
              <li><strong>Deletion ("Right to be Forgotten"):</strong> Request deletion of your account and associated data</li>
              <li><strong>Portability:</strong> Receive your data in a structured, machine-readable format (JSON)</li>
              <li><strong>Objection to processing:</strong> Object to processing based on legitimate interests</li>
              <li><strong>Withdraw consent:</strong> Where processing is based on consent, you may withdraw it at any time</li>
            </ul>
            <SubHead>8.2 How to Exercise Your Rights</SubHead>
            <p className="text-sm text-gray-700">
              Submit a request to <a href={`mailto:${EMAIL}`} className="text-brand-green underline hover:underline">{EMAIL}</a> with the subject "Privacy Rights Request."
              We will respond within 30 days (GDPR) or 45 days (CCPA). We may verify your identity before processing the request.
              There is no fee for exercising your rights; however, we may charge a reasonable administrative fee for manifestly
              unfounded or excessive requests.
            </p>
          </Section>

          <Section id="cookies" title="9. Cookies & Tracking">
            {/* 1.4.10 Reflow: contained + keyboard-scrollable at 320px */}
            <div
              className="w-full overflow-x-auto focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-navy rounded-lg"
              tabIndex={0}
              role="region"
              aria-label="Cookie purposes"
              data-testid="privacy-table-scroll"
            >
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-brand-navy/5">
                  <th className="text-left p-3 font-semibold text-brand-navy border border-gray-200">Cookie Type</th>
                  <th className="text-left p-3 font-semibold text-brand-navy border border-gray-200">Purpose</th>
                  <th className="text-left p-3 font-semibold text-brand-navy border border-gray-200">Can Opt Out?</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Strictly Necessary", "Authentication (JWT session token), security", "No — required for service"],
                  ["Functional", "Language preference, UI settings", "No — required for service"],
                  ["Analytics (PostHog)", "Product usage analytics, session recording", "Yes — email us to opt out"],
                  ["Performance", "Error tracking (Sentry if enabled)", "Yes"],
                ].map(([t, p, o]) => (
                  <tr key={t} className="hover:bg-gray-50">
                    <td className="p-3 font-medium text-gray-700 border border-gray-200">{t}</td>
                    <td className="p-3 text-gray-500 border border-gray-200">{p}</td>
                    <td className="p-3 text-gray-500 border border-gray-200">{o}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            <p className="mt-3 text-sm text-gray-700">
              We do not use third-party advertising cookies or sell cookie-derived data to advertisers.
            </p>
          </Section>

          <Section id="security" title="10. Data Security">
            <p className="text-sm text-gray-700">
              {COMPANY} implements commercially reasonable technical and organizational measures to protect
              your personal data against unauthorized access, loss, or destruction. These include:
            </p>
            <ul className="list-disc pl-5 space-y-1 text-gray-700 text-sm mt-3">
              <li>Passwords hashed with bcrypt (cost factor ≥ 12)</li>
              <li>HTTPS/TLS 1.2+ encryption for all data in transit</li>
              <li>MongoDB Atlas encryption at rest (AES-256)</li>
              <li>JWT authentication with short-lived tokens and httpOnly cookies</li>
              <li>Rate limiting on authentication endpoints to prevent brute force</li>
              <li>Role-based access control (admin vs. standard user)</li>
              <li>90-day server log rotation</li>
            </ul>
            <p className="mt-3 text-sm text-gray-700">
              No method of transmission over the Internet is 100% secure. In the event of a data breach
              that affects your rights and freedoms, we will notify you and applicable regulators within
              72 hours of discovery, as required by GDPR Art. 33-34.
            </p>
          </Section>

          <Section id="children" title="11. Children's Privacy">
            <p className="text-sm text-gray-700">
              The Platform is not directed to individuals under the age of 13. We do not knowingly collect
              personal data from children under 13. If you are between 13 and 18, you must have verifiable
              parental or guardian consent to use the Platform.
            </p>
            <p className="mt-3 text-sm text-gray-700">
              If we discover that we have inadvertently collected personal data from a child under 13,
              we will delete it immediately. If you believe we may have such data, please contact us at{" "}
              <a href={`mailto:${EMAIL}`} className="text-brand-green underline hover:underline">{EMAIL}</a>.
            </p>
          </Section>

          <Section id="international" title="12. International Data Transfers">
            <p className="text-sm text-gray-700">
              {COMPANY} is based in the United States. If you access the Platform from outside the US,
              your personal data will be transferred to and processed in the United States, which may
              not have equivalent data protection laws to your jurisdiction.
            </p>
            <p className="mt-3 text-sm text-gray-700">
              For transfers from the EEA to the US, we rely on Standard Contractual Clauses (SCCs)
              approved by the European Commission as the transfer mechanism. A copy of our SCCs is
              available upon request.
            </p>
          </Section>

          <Section id="changes" title="13. Changes to This Policy">
            <p className="text-sm text-gray-700">
              We reserve the right to update this Policy at any time. We will notify you of material
              changes by posting the updated Policy on this page with a revised "Last Updated" date,
              and, where required by law, by sending you an email notification at least 30 days before
              the changes take effect.
            </p>
            <p className="mt-3 text-sm text-gray-700">
              Your continued use of the Platform after any changes constitutes your acceptance of the
              revised Policy.
            </p>
          </Section>

          <Section id="contact" title="14. Contact Us">
            <p className="text-sm text-gray-700">
              For privacy-related inquiries, data subject requests, or complaints, please contact our
              Privacy Team:
            </p>
            <div className="mt-4 bg-brand-navy/5 border border-brand-navy/20 rounded-xl p-5 space-y-2">
              <p className="font-bold text-brand-navy">{COMPANY}</p>
              <p className="text-sm text-gray-600">Attn: Privacy Officer</p>
              <div className="flex items-center gap-2 text-sm text-gray-600"><MapPin size={13} />{ADDRESS}</div>
              <div className="flex items-center gap-2 text-sm text-gray-600"><Mail size={13} /><a href={`mailto:${EMAIL}`} className="text-brand-green underline hover:underline">{EMAIL}</a></div>
            </div>
            <p className="mt-3 text-sm text-gray-700">
              You also have the right to lodge a complaint with your local data protection authority.
              For EU residents, find your supervisory authority at{" "}
              <a href="https://edpb.europa.eu/about-edpb/about-edpb/members_en" className="text-brand-green underline hover:underline" target="_blank" rel="noopener noreferrer">edpb.europa.eu</a>.
            </p>
          </Section>

          <div className="pt-8 border-t border-gray-200 text-xs text-gray-400">
            <p>© {new Date().getFullYear()} {COMPANY} · {DBA} · All rights reserved</p>
            <div className="flex gap-4 mt-2">
              <Link to="/terms" className="hover:text-brand-green">Terms of Service</Link>
              <Link to="/safety" className="hover:text-brand-green">Safety Center</Link>
              <Link to="/community" className="hover:text-brand-green">Community Guidelines</Link>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-xl font-heading font-bold text-brand-navy mb-4 pb-2 border-b border-gray-200">{title}</h2>
      <div className="text-gray-700 leading-relaxed">{children}</div>
    </section>
  );
}

function SubHead({ children }) {
  return <h3 className="font-semibold text-brand-navy mt-4 mb-2 text-sm">{children}</h3>;
}
